import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  AttachmentField,
  AttachmentGallery,
  Badge,
  Button,
  CopyButton,
  DateField,
  DeskModal,
  Fact,
  Facts,
  Field,
  Icon,
  Modal,
  ModalActions,
  NotProvided,
  PersonMark,
  RelatedGroup,
  RelatedItem,
  SearchSelect,
  TextArea,
  buttonClasses,
  readAttachmentContent,
  type AttachmentItem,
} from '@evogard/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import type {
  ChargeSummary,
  FinanceExpenseActivity,
  RevenueDetail,
  RevenueFile,
} from '@evogard/contract';
import {
  CHARGE_STATUS_LABELS,
  REVENUE_SOURCE_LABELS,
  REVENUE_STATUS_LABELS,
  REVENUE_STATUS_TONES,
  billingTypeLabel,
  businessDate,
  format,
  formatDate,
  formatInstant,
  fromColumn,
  parseMoneyInput,
} from '@evogard/domain';
import { SettlementFacts } from '../../../components/ChargeSettlement.js';
import { LabelsPicker } from '../../../components/labels/LabelsPicker.js';
import { api, ok } from '../../../lib/api.js';
import { can, useSessionContext } from '../../../lib/session.js';
import {
  revenueFileUrl,
  useRevenue,
  useRevenueActivity,
  useRevenueAssigneeOptions,
  useRevenueDeskMutations,
  useRevenueFiles,
  useRevenueLabelOptions,
  type RevenueDeskMutations,
} from './useRevenueDesk.js';
import { REVENUES_KEY } from './RevenuesScreen.js';

type Tab = 'cobrancas' | 'atividade' | 'anexos';
type Dialog = 'baixa' | 'cancelar' | null;

const money = (v: string) => format(fromColumn(v)!);

/**
 * A mesa da receita no Financeiro (29/10/2026) — a anatomia da mesa da
 * despesa, a pedido do dono:
 *
 * - **no alto**: o responsável, o vencimento e as etiquetas dos setores de
 *   quem olha;
 * - **à esquerda**: de onde a receita vem (a PI de um acionamento de
 *   Periféricos, ou lançada à mão), o resumo dela e o Conta Azul;
 * - **no miolo**: Cobranças (as do Asaas, com a baixa de cada uma), Atividade
 *   (o caminho e os comentários) e Anexos;
 * - **no rodapé**: os atos da receita — cobrar, dar baixa, cancelar e enviar
 *   ao Conta Azul. Não há o movimento de avançar e voltar da despesa: a
 *   situação muda pelo ato, ou pela cobrança paga.
 *
 * Etiqueta, responsável, comentário e anexo gravam na hora. Quem só vê
 * (`finance.revenues:ver`) vê tudo, sem os controles.
 */
export function RevenueDesk({ id, onClose }: { id: number; onClose: () => void }) {
  const { data: context } = useSessionContext();
  const writes = can(context, 'finance.revenues', 'lancar');
  const detail = useRevenue(id);
  const activity = useRevenueActivity(id);
  const files = useRevenueFiles(id);
  const labelOptions = useRevenueLabelOptions(writes);
  const mutations = useRevenueDeskMutations(id);
  const [tab, setTab] = useState<Tab>('cobrancas');
  const [dialog, setDialog] = useState<Dialog>(null);
  const [error, setError] = useState<string | null>(null);
  const r = detail.data;

  const count = (n: number | undefined) =>
    n === undefined ? null : (
      <span className="ml-1.5 rounded-pilula bg-superficie-suave px-1.5 text-xs tabular-nums text-texto-suave">
        {n}
      </span>
    );

  const run = (m: { mutate: (v: void, o: { onError: (e: Error) => void }) => void }) => {
    setError(null);
    m.mutate(undefined, { onError: (e) => setError(e.message) });
  };

  const actions =
    r && (r.can.charge || r.can.settle || r.can.cancel || r.can.syncContaAzul);

  return (
    <DeskModal
      open
      onOpenChange={(open) => !open && onClose()}
      title={
        r ? (
          <span className="inline-flex flex-wrap items-center gap-2">
            <span className="text-sm text-texto-suave tabular-nums">#{r.id}</span>
            {r.payer.name ?? 'Sem nome'}
          </span>
        ) : (
          'Carregando…'
        )
      }
      descricao={r?.description}
      belowTitle={
        r && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <AssigneePicker
              revenue={r}
              enabled={writes}
              onChange={(v) => mutations.setAssignee.mutate(v)}
              saving={mutations.setAssignee.isPending}
            />
            <span
              className="text-sm text-texto-suave tabular-nums"
              title="O vencimento da receita"
            >
              Vence em {formatDate(r.dueDate)}
            </span>
            <LabelsPicker
              labels={r.labels}
              editable={writes}
              save={mutations.setLabels}
              options={labelOptions}
            />
          </div>
        )
      }
      leftLabel="A receita"
      left={r ? <Summary revenue={r} /> : null}
      middleLabel="Conteúdo da receita"
      footer={
        actions ? (
          <div className="flex flex-col gap-2">
            {error && <Alert tone="erro">{error}</Alert>}
            <div className="flex flex-wrap justify-end gap-2">
              {r.can.syncContaAzul && (
                <Button
                  variant="contorno"
                  loading={mutations.syncContaAzul.isPending}
                  onClick={() => run(mutations.syncContaAzul)}
                >
                  Enviar ao Conta Azul
                </Button>
              )}
              {r.can.cancel && (
                <Button variant="perigo" onClick={() => setDialog('cancelar')}>
                  Cancelar receita
                </Button>
              )}
              {r.can.settle && (
                <Button variant="contorno" onClick={() => setDialog('baixa')}>
                  Dar baixa
                </Button>
              )}
              {r.can.charge && (
                <Button
                  loading={mutations.charge.isPending}
                  onClick={() => run(mutations.charge)}
                >
                  Cobrar no Asaas
                </Button>
              )}
            </div>
          </div>
        ) : undefined
      }
    >
      {detail.isError ? (
        <Alert tone="erro">{detail.error.message}</Alert>
      ) : !r ? (
        <p className="text-sm text-texto-suave">Carregando a receita…</p>
      ) : (
        <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
          <TabsList>
            <TabsTrigger value="cobrancas">
              Cobranças{count(r.charges.length)}
            </TabsTrigger>
            <TabsTrigger value="atividade">
              Atividade{count(activity.data?.length)}
            </TabsTrigger>
            <TabsTrigger value="anexos">Anexos{count(files.data?.length)}</TabsTrigger>
          </TabsList>
          <TabsContent value="cobrancas" className="mt-4 flex flex-col gap-3">
            <ChargesTab revenue={r} />
          </TabsContent>
          <TabsContent value="atividade" className="mt-4">
            <ActivityTab
              items={activity.data}
              error={activity.error}
              canComment={writes}
              mutations={mutations}
              me={context?.user.id ?? null}
            />
          </TabsContent>
          <TabsContent value="anexos" className="mt-4">
            <FilesTab
              revenueId={r.id}
              files={files.data}
              error={files.error}
              canAttach={writes}
              mutations={mutations}
            />
          </TabsContent>
        </Tabs>
      )}
      {dialog === 'baixa' && r && (
        <SettleDialog revenue={r} onClose={() => setDialog(null)} />
      )}
      {dialog === 'cancelar' && r && (
        <CancelDialog revenue={r} onClose={() => setDialog(null)} />
      )}
    </DeskModal>
  );
}

function AssigneePicker({
  revenue,
  enabled,
  onChange,
  saving,
}: {
  revenue: RevenueDetail;
  enabled: boolean;
  onChange: (userId: string | null) => void;
  saving: boolean;
}) {
  const options = useRevenueAssigneeOptions(enabled);
  const assignee = revenue.assignee;
  if (!enabled) {
    return (
      <PersonMark
        name={assignee?.name ?? null}
        emptyLabel="Sem responsável"
        className="max-w-56 text-sm"
      />
    );
  }
  /* Quem já é responsável e deixou de poder lançar continua aparecendo. */
  const list = options.data ?? [];
  const choices =
    assignee && !list.some((x) => x.id === assignee.id) ? [assignee, ...list] : list;
  return (
    <SearchSelect
      label="Responsável"
      options={choices.map((x) => ({ value: x.id, label: x.name }))}
      value={assignee?.id ?? ''}
      onChange={(v) => onChange(v === '' ? null : v)}
      searchPlaceholder="Buscar pessoa"
      emptyMessage="Ninguém que lança receita com esse nome."
      loading={options.isLoading}
      clearable
      sortable
      compact={{
        icon: 'pessoa',
        filled: assignee !== null,
        text: assignee?.name,
        title: assignee ? `Responsável: ${assignee.name}` : 'Escolher o responsável',
        loading: saving,
      }}
    />
  );
}

/** A coluna da esquerda: de onde a receita vem e o que se consulta a cada passo. */
function Summary({ revenue: r }: { revenue: RevenueDetail }) {
  return (
    <div className="flex flex-col gap-4 px-1">
      <OriginGroup revenue={r} />
      <RelatedGroup title="Situação">
        <div className="flex flex-wrap gap-1.5 px-2">
          <Badge tone={REVENUE_STATUS_TONES[r.status]}>
            {REVENUE_STATUS_LABELS[r.status]}
          </Badge>
          {r.lastChargeStatus && (
            <Badge tone="neutro">Asaas: {CHARGE_STATUS_LABELS[r.lastChargeStatus]}</Badge>
          )}
        </div>
        {r.status === 'cancelada' && r.cancelReason && (
          <p className="px-2 pt-1 text-xs text-texto-suave">Motivo: {r.cancelReason}</p>
        )}
      </RelatedGroup>
      <dl className="flex flex-col gap-3 px-2 text-sm">
        <Fact label="Valor" icon="dinheiro">
          <span className="tabular-nums">{money(r.amount)}</span>
        </Fact>
        <Fact label="Vencimento" icon="calendario">
          <span className="tabular-nums">{formatDate(r.dueDate)}</span>
        </Fact>
        <Fact label="Competência" icon="calendario">
          <span className="tabular-nums">{formatDate(r.competencyDate)}</span>
        </Fact>
        <Fact label="Quem paga" icon="pessoa">
          <span className="font-semibold">{r.payer.name ?? 'Sem nome'}</span>
          <span className="block text-xs text-texto-suave">
            {r.payer.kind === 'associado'
              ? [r.payer.code, r.payer.documentMasked].filter(Boolean).join(' · ')
              : ['Beneficiário', r.payer.documentMasked].filter(Boolean).join(' · ')}
          </span>
        </Fact>
        <Fact label="Categoria" icon="documento">
          {r.category?.name ?? <NotProvided />}
        </Fact>
        <Fact label="Conta de recebimento" icon="dinheiro">
          {r.paymentAccount.name}
        </Fact>
        {r.receivedAmount && r.receivedDate && (
          <Fact label="Recebido" icon="confirmado">
            <span className="tabular-nums">
              {money(r.receivedAmount)} em {formatDate(r.receivedDate)}
            </span>
          </Fact>
        )}
        {r.notes && (
          <Fact label="Observações" icon="documento">
            <span className="font-normal whitespace-pre-wrap">{r.notes}</span>
          </Fact>
        )}
      </dl>
      <ContaAzulState revenue={r} />
    </div>
  );
}

/**
 * De onde a receita veio: a PI de um acionamento de Periféricos — que abre em
 * outra aba, para o Financeiro não perder a mesa, e só para quem pode ver o
 * acionamento — ou lançada à mão pelo Financeiro.
 */
function OriginGroup({ revenue: r }: { revenue: RevenueDetail }) {
  const { data: context } = useSessionContext();
  const visual = <Icon nome="documento" size={20} className="mt-0.5 text-texto-suave" />;
  const o = r.origin;
  if (o.kind === 'avulsa') {
    return (
      <RelatedGroup title="De onde vem">
        <RelatedItem
          name="Lançada à mão no Financeiro"
          visual={visual}
          subtitle={[o.createdByName, formatInstant(r.createdAt)]
            .filter(Boolean)
            .join(' · ')}
        />
      </RelatedGroup>
    );
  }
  if (o.kind === 'periferico') {
    const canOpen = can(context, 'claims.peripherals', 'ver');
    return (
      <RelatedGroup title="De onde vem">
        <RelatedItem
          name={`Periférico #${o.peripheralId}`}
          visual={visual}
          subtitle={
            o.linkReplaced
              ? 'PI do acionamento · link substituído por um novo'
              : 'PI do acionamento de Periféricos'
          }
          onOpen={
            canOpen
              ? () =>
                  window.open(
                    `/claims/peripherals?open=${o.peripheralId}`,
                    '_blank',
                    'noopener',
                  )
              : undefined
          }
          openLabel={`Abrir o acionamento #${o.peripheralId} em outra aba`}
        />
      </RelatedGroup>
    );
  }
  return (
    <RelatedGroup
      title="De onde vem"
      empty={`${REVENUE_SOURCE_LABELS[r.sourceType]}: o pedido que originou esta receita não foi achado.`}
    />
  );
}

/** O nome de cada situação da receita no Conta Azul. */
// @valor
const CONTA_AZUL_LABELS: Record<RevenueDetail['contaAzul']['state'], string> = {
  nao_enviada: 'Ainda não enviada ao Conta Azul',
  enviando: 'Enviando ao Conta Azul',
  lancada: 'Lançada no Conta Azul',
  recusada: 'Não foi para o Conta Azul',
};

/** O que a API diz da receita no Conta Azul — a tela não deduz. */
function ContaAzulState({ revenue: r }: { revenue: RevenueDetail }) {
  const c = r.contaAzul;
  const tone =
    c.state === 'lancada' ? 'sucesso' : c.state === 'recusada' ? 'alerta' : 'informacao';
  return (
    <Alert tone={tone} title={CONTA_AZUL_LABELS[c.state]}>
      {c.state === 'lancada'
        ? c.settled
          ? 'Conta a receber criada lá, já com a baixa.'
          : (c.message ?? 'A baixa lá ainda não foi dada.')
        : (c.message ?? 'A receita vai ao Conta Azul quando for recebida.')}
    </Alert>
  );
}

function ChargesTab({ revenue: r }: { revenue: RevenueDetail }) {
  const { refetch } = useRevenue(r.id);
  return (
    <>
      {r.charges.length === 0 ? (
        <p className="text-sm text-texto-suave">Nenhuma cobrança emitida no Asaas.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {r.charges.map((c) => (
            <ChargeItem key={c.id} charge={c} onRefreshed={() => void refetch()} />
          ))}
        </ul>
      )}
      {r.chargeBlockedReason && r.status !== 'cancelada' && r.status !== 'recebida' && (
        <p className="text-sm text-texto-suave">{r.chargeBlockedReason}</p>
      )}
      {r.receiptFileName && (
        <p className="text-sm text-texto-suave">
          Baixa à mão com comprovante: {r.receiptFileName} — está em Anexos.
        </p>
      )}
    </>
  );
}

function ChargeItem({
  charge: c,
  onRefreshed,
}: {
  charge: ChargeSummary;
  onRefreshed: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const refresh = useMutation({
    mutationFn: async () =>
      ok(
        await api.charges.refreshCharge({ params: { id: c.id }, body: {} }),
        200,
        'Não foi possível reler a cobrança.',
      ),
    onSuccess: () => {
      setError(null);
      onRefreshed();
    },
    onError: (e: Error) => setError(e.message),
  });
  return (
    <li className="flex flex-col gap-3 rounded-md border border-borda p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex flex-col">
          <span className="text-texto">
            {money(c.amount)} · vence em {formatDate(c.dueDate)}
          </span>
          <span className="text-sm text-texto-suave">
            {CHARGE_STATUS_LABELS[c.status]}
            {c.status === 'desconhecida' &&
              c.providerStatus &&
              ` (Asaas: ${c.providerStatus})`}
            {c.confirmedAt &&
              ` · ${billingTypeLabel(c.billingType) ?? 'pago'} em ${formatInstant(c.confirmedAt)}`}
          </span>
        </span>
        <span className="flex items-center gap-1.5">
          {c.invoiceUrl && (
            <CopyButton text={c.invoiceUrl} label="Copiar o link de pagamento" />
          )}
          {c.invoiceUrl && (
            <a
              href={c.invoiceUrl}
              target="_blank"
              rel="noreferrer"
              className={buttonClasses({ variant: 'contorno', size: 'sm' })}
            >
              Abrir
            </a>
          )}
          {c.status !== 'falhou' && (
            <Button
              variant="contorno"
              size="sm"
              loading={refresh.isPending}
              onClick={() => refresh.mutate()}
            >
              Reler
            </Button>
          )}
        </span>
      </div>
      {c.error && (c.status === 'falhou' || c.status === 'emitindo') && (
        <p className="text-sm text-texto-suave">{c.error}</p>
      )}
      {error && <Alert tone="erro">{error}</Alert>}
      {c.settlement && (
        <Facts>
          <SettlementFacts
            settlement={c.settlement}
            openReceipt={async () =>
              ok(
                await api.charges.chargeReceiptUrl({ params: { id: c.id } }),
                200,
                'Não foi possível abrir o comprovante.',
              ).url
            }
          />
        </Facts>
      )}
    </li>
  );
}

function ActivityTab({
  items,
  error,
  canComment,
  mutations,
  me,
}: {
  items: FinanceExpenseActivity[] | undefined;
  error: Error | null;
  canComment: boolean;
  mutations: RevenueDeskMutations;
  me: string | null;
}) {
  const [text, setText] = useState('');
  const [editing, setEditing] = useState<{ id: number; text: string } | null>(null);
  if (error) return <Alert tone="erro">{error.message}</Alert>;
  return (
    <div className="flex flex-col gap-4">
      {canComment && (
        <div className="flex flex-col gap-2">
          <TextArea
            label="Comentar"
            placeholder="Escreva um comentário sobre a receita"
            value={text}
            onChange={(ev) => setText(ev.target.value)}
            maxLength={4000}
            rows={2}
          />
          {mutations.comment.isError && (
            <Alert tone="erro">{mutations.comment.error.message}</Alert>
          )}
          <div className="flex justify-end">
            <Button
              size="sm"
              disabled={text.trim() === ''}
              loading={mutations.comment.isPending}
              onClick={() =>
                mutations.comment.mutate(text, { onSuccess: () => setText('') })
              }
            >
              Comentar
            </Button>
          </div>
        </div>
      )}
      {!items ? (
        <p className="text-sm text-texto-suave">Carregando a atividade…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-texto-suave">Nada registrado ainda.</p>
      ) : (
        <ol className="flex flex-col gap-3">
          {items.map((item) =>
            item.kind === 'comentario' ? (
              <li
                key={item.key}
                className="rounded-borda border border-borda bg-superficie px-3 py-2.5 text-sm"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-semibold">
                    {item.comment.authorName ?? 'Alguém'}
                  </span>
                  <span className="text-xs text-texto-suave tabular-nums">
                    {formatInstant(item.at)}
                    {item.comment.editedAt ? ' · editado' : ''}
                  </span>
                </div>
                {item.comment.archivedAt ? (
                  <p className="mt-1 text-texto-suave italic">Comentário removido.</p>
                ) : editing?.id === item.comment.id ? (
                  <div className="mt-2 flex flex-col gap-2">
                    <TextArea
                      label="Corrigir o comentário"
                      value={editing.text}
                      onChange={(ev) =>
                        setEditing({ id: editing.id, text: ev.target.value })
                      }
                      maxLength={4000}
                      rows={2}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="secundario"
                        onClick={() => setEditing(null)}
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        disabled={editing.text.trim() === ''}
                        loading={mutations.updateComment.isPending}
                        onClick={() =>
                          mutations.updateComment.mutate(
                            { commentId: editing.id, change: { body: editing.text } },
                            { onSuccess: () => setEditing(null) },
                          )
                        }
                      >
                        Salvar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="mt-1 whitespace-pre-wrap">{item.comment.body}</p>
                    {canComment && item.comment.authorId === me && (
                      <div className="mt-1.5 flex gap-1">
                        <Button
                          size="sm"
                          variant="discreto"
                          onClick={() =>
                            setEditing({ id: item.comment.id, text: item.comment.body })
                          }
                        >
                          Corrigir
                        </Button>
                        <Button
                          size="sm"
                          variant="discreto"
                          onClick={() =>
                            mutations.updateComment.mutate({
                              commentId: item.comment.id,
                              change: { archived: true },
                            })
                          }
                        >
                          Remover
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </li>
            ) : (
              <li key={item.key} className="flex flex-col px-1 text-sm">
                <span>
                  <span className="font-semibold">{item.summary}</span>
                  {item.detail ? (
                    <span className="text-texto-suave"> · {item.detail}</span>
                  ) : null}
                </span>
                <span className="text-xs text-texto-suave tabular-nums">
                  {formatInstant(item.at)} · {item.actorName ?? 'o sistema'}
                </span>
              </li>
            ),
          )}
        </ol>
      )}
    </div>
  );
}

function fileItem(revenueId: number, f: RevenueFile, label?: string): AttachmentItem {
  return {
    key: f.key,
    nome: f.fileName,
    typeMime: f.mimeType ?? 'application/octet-stream',
    sizeBytes: f.sizeBytes ?? 0,
    detail:
      [label, f.createdByName, f.createdAt ? formatInstant(f.createdAt) : null]
        .filter(Boolean)
        .join(' · ') || undefined,
    resolveUrl: () => revenueFileUrl(revenueId, f.key),
  };
}

/** Os Anexos: o comprovante da baixa à mão, só para ver, e os anexos do Financeiro. */
function FilesTab({
  revenueId,
  files,
  error,
  canAttach,
  mutations,
}: {
  revenueId: number;
  files: RevenueFile[] | undefined;
  error: Error | null;
  canAttach: boolean;
  mutations: RevenueDeskMutations;
}) {
  const [sending, setSending] = useState(false);
  if (error) return <Alert tone="erro">{error.message}</Alert>;
  if (!files) return <p className="text-sm text-texto-suave">Carregando os anexos…</p>;
  const receipt = files.filter((f) => f.slot === 'comprovante');
  const general = files
    .filter((f) => f.slot === 'anexo')
    .map((f) => fileItem(revenueId, f));

  const send = async (picked: File[]) => {
    setSending(true);
    try {
      /* Um de cada vez: o envio parcial diz qual arquivo não foi. */
      for (const file of picked) await mutations.attach.mutateAsync(file);
    } catch {
      /* a mensagem fica no `mutations.attach.error`, abaixo */
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {receipt.length > 0 && (
        <AttachmentGallery
          label="Comprovante da baixa"
          items={receipt.map((f) => fileItem(revenueId, f, 'Baixa à mão'))}
          empty={null}
        />
      )}
      {canAttach ? (
        <AttachmentField
          label="Anexos"
          items={general}
          max={50}
          busy={sending}
          disabled={sending}
          onAdd={(picked) => void send(picked)}
          onRemove={(key) => mutations.archiveFile.mutate(Number(key))}
        />
      ) : (
        <AttachmentGallery
          label="Anexos"
          items={general}
          empty={<p className="text-sm text-texto-suave">Nenhum anexo.</p>}
        />
      )}
      {(mutations.attach.isError || mutations.archiveFile.isError) && (
        <Alert tone="erro">
          {(mutations.attach.error ?? mutations.archiveFile.error)?.message}
        </Alert>
      )}
    </div>
  );
}

/** A baixa à mão: o dinheiro entrou fora do Asaas, e o comprovante prova. */
function SettleDialog({
  revenue,
  onClose,
}: {
  revenue: RevenueDetail;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const today = businessDate(new Date());
  const [amount, setAmount] = useState(revenue.amount.replace('.', ','));
  const [date, setDate] = useState(today);
  const [file, setFile] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const value = parseMoneyInput(amount);
  const problems = {
    amount: value !== null && value > 0 ? null : 'Informe um valor maior que zero.',
    date: !date ? 'Informe a data.' : date > today ? 'A data não pode ser futura.' : null,
    file: file ? null : 'Anexe o comprovante.',
  };
  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.revenues.settleRevenue({
          params: { id: revenue.id },
          body: {
            amount,
            date,
            file: {
              fileName: file!.name,
              mimeType: file!.type || 'application/octet-stream',
              content: await readAttachmentContent(file!),
            },
          },
        }),
        200,
        'Não foi possível dar a baixa.',
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: REVENUES_KEY });
      onClose();
    },
  });
  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Dar baixa"
      descricao="O dinheiro entrou fora do Asaas — depósito, Pix na conta, dinheiro. Informe o que entrou e anexe o comprovante."
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(true);
          if (Object.values(problems).every((p) => p === null)) save.mutate();
        }}
      >
        {save.error && <Alert tone="erro">{save.error.message}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Valor recebido"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            prefix="R$"
            autoComplete="off"
            error={submitted ? problems.amount : null}
          />
          <DateField
            label="Recebido em"
            value={date}
            onChange={setDate}
            layer="modal"
            error={submitted ? problems.date : null}
          />
        </div>
        <Field
          label="Comprovante"
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          error={submitted ? problems.file : null}
        />
        <ModalActions>
          <Button type="button" variant="contorno" onClick={onClose}>
            Voltar
          </Button>
          <Button type="submit" loading={save.isPending}>
            Dar baixa
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
}

function CancelDialog({
  revenue,
  onClose,
}: {
  revenue: RevenueDetail;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const problem = reason.trim().length >= 3 ? null : 'Escreva o motivo.';
  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.revenues.cancelRevenue({
          params: { id: revenue.id },
          body: { reason },
        }),
        200,
        'Não foi possível cancelar a receita.',
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: REVENUES_KEY });
      onClose();
    },
  });
  const open = revenue.charges.some((c) =>
    ['emitindo', 'pendente', 'vencida'].includes(c.status),
  );
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Cancelar receita"
      descricao={
        open
          ? 'A receita deixa de ser esperada. A cobrança aberta é cancelada no Asaas, e o link de pagamento para de funcionar.'
          : 'A receita deixa de ser esperada.'
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(true);
          if (!problem) save.mutate();
        }}
      >
        {save.error && <Alert tone="erro">{save.error.message}</Alert>}
        <TextArea
          label="Motivo"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          rows={2}
          error={submitted ? problem : null}
        />
        <ModalActions>
          <Button type="button" variant="contorno" onClick={onClose}>
            Voltar
          </Button>
          <Button type="submit" variant="perigo" loading={save.isPending}>
            Cancelar receita
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
}
